import React, { useState, useEffect } from "react";
import { Modal, Button, Form } from "react-bootstrap";
import AssignAppointmentModal from "./AssignAppointmentModal.jsx";
import PaymentModal from "./PaymentModal.jsx";
import { useDispatch } from "react-redux";
import {
  assignAppointment,
  rejectAppointment,
  completeAppointment,
  recordPayment,
} from "../../../Redux/actions/adminActions/adminAppointmentActions.js";
import { showToast } from "../../ToastMessage.jsx";
import { printAppointmentSlip } from "./PrintAppointmentSlip.js";
import { printPaymentReceipt } from "./PrintPaymentReceipt.js";

const ViewAppointmentModal = ({ show, onHide, appointment }) => {
  const dispatch = useDispatch();

  // LOCAL STATE COPY TO REFLECT INSTANT CHANGES
  const [localAppointment, setLocalAppointment] = useState(appointment);

  useEffect(() => {
    setLocalAppointment(appointment);
    setRejectReason(appointment?.rejectionReason || ""); // sync existing rejection reason
  }, [appointment]);

  const [assignShow, setAssignShow] = useState(false);
  const [paymentShow, setPaymentShow] = useState(false);

  // FIXED: rejection reason always defined & synced
  const [rejectReason, setRejectReason] = useState("");
  const [showRejectBox, setShowRejectBox] = useState(false);

  if (!localAppointment) return null;

  const canAssign =
    localAppointment.status === "pending" ||
    localAppointment.status === "confirmed";

  const canReject = localAppointment.status !== "completed";

  const canComplete =
    localAppointment.status === "confirmed" &&
    localAppointment.payment?.status === "paid";

  const canPrintAppointment =
    localAppointment.status === "confirmed" ||
    localAppointment.status === "completed";

  const hasPayment = localAppointment.payment?.status === "paid";

  // ====== HANDLERS WITH INSTANT UI UPDATE ======

  const handleAssign = async (data) => {
    try {
      const updated = await dispatch(
        assignAppointment(localAppointment._id, data)
      );

      setLocalAppointment((prev) => ({
        ...prev,
        ...updated,
        status: "confirmed",
        date: data.date,
        time: data.time,
        doctor: updated.doctor,
      }));

      showToast("Assigned & confirmed");
      setAssignShow(false);
    } catch (err) {
      showToast(err.message || "Assign failed", "error");
    }
  };

  const handleReject = async () => {
    if (!rejectReason || rejectReason.trim().length < 3) {
      return showToast("Please enter a valid reason", "error");
    }

    try {
      const updated = await dispatch(
        rejectAppointment(localAppointment._id, rejectReason)
      );

      setLocalAppointment((prev) => ({
        ...prev,
        status: "rejected",
        rejectionReason: rejectReason,
      }));

      showToast("Appointment rejected");
      setShowRejectBox(false);
    } catch (err) {
      showToast(err.message || "Reject failed", "error");
    }
  };

  const handleComplete = async () => {
    try {
      await dispatch(completeAppointment(localAppointment._id));

      setLocalAppointment((prev) => ({
        ...prev,
        status: "completed",
      }));

      showToast("Appointment marked completed");
    } catch (err) {
      showToast(err.message || "Complete failed", "error");
    }
  };

  const handleSavePayment = async (amount) => {
    try {
      await dispatch(recordPayment(localAppointment._id, amount));

      setLocalAppointment((prev) => ({
        ...prev,
        payment: { status: "paid", feePaid: amount },
      }));

      showToast("Payment recorded");
      setPaymentShow(false);
    } catch (err) {
      showToast(err.message || "Payment failed", "error");
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Appointment Details</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {/* DETAILS */}
          <div className="row mb-3">
            <div className="col-md-6">
              <p>
                <strong>Patient:</strong> {localAppointment.pName}
              </p>
              <p>
                <strong>Father Name:</strong> {localAppointment.fatherName}
              </p>
              <p>
                <strong>Contact:</strong> {localAppointment.contact}
              </p>
              <p>
                <strong>Address:</strong> {localAppointment.address}
              </p>
            </div>

            <div className="col-md-6">
              <p>
                <strong>Department:</strong> {localAppointment.department}
              </p>
              <p>
                <strong>Doctor:</strong> {localAppointment.doctor?.name || "-"}
              </p>
              <p>
                <strong>Date:</strong> {localAppointment.date || "-"}
              </p>
              <p>
                <strong>Time:</strong> {localAppointment.time || "-"}
              </p>
              <p>
                <strong>Appointment #:</strong>{" "}
                {localAppointment.appointmentNumber || "-"}
              </p>
            </div>
          </div>

          {/* PAYMENT INFO */}
          <div className="mb-3">
            <p>
              <strong>Payment:</strong> Rs.{" "}
              {localAppointment.payment?.feePaid ?? 0} —{" "}
              <strong>
                {localAppointment.payment?.status === "paid"
                  ? "Paid"
                  : "Unpaid"}
              </strong>
            </p>

            {/* SHOW EXISTING REJECTION REASON */}
            {localAppointment.rejectionReason && (
              <div className="alert alert-danger">
                <strong>Rejection Reason:</strong> <br />
                {localAppointment.rejectionReason}
              </div>
            )}
          </div>

          {/* BUTTONS */}
          <div className="d-flex flex-wrap gap-2">
            <Button
              variant="outline-primary"
              onClick={() => setAssignShow(true)}
              disabled={!canAssign}
            >
              Assign / Reassign
            </Button>

            <Button
              variant="outline-success"
              onClick={() => setPaymentShow(true)}
              disabled={
                localAppointment.status === "rejected" ||
                localAppointment.status === "completed"
              }
            >
              Add Payment
            </Button>

            <Button
              variant="outline-primary"
              onClick={handleComplete}
              disabled={!canComplete}
            >
              Mark Completed
            </Button>

            <Button
              variant="info"
              onClick={() => printAppointmentSlip(localAppointment)}
              disabled={!canPrintAppointment}
            >
              Print Appointment
            </Button>

            <Button
              variant="warning"
              onClick={() => printPaymentReceipt(localAppointment)}
              disabled={!hasPayment}
            >
              Print Payment
            </Button>

            <Button
              variant="danger"
              onClick={() => setShowRejectBox(true)}
              disabled={!canReject}
            >
              {localAppointment.status === "rejected"
                ? "Update Rejection"
                : "Reject"}
            </Button>
          </div>

          {/* REJECTION TEXTBOX */}
          {showRejectBox && (
            <div className="mt-3">
              <Form.Group>
                <Form.Label>Rejection Reason</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                />
              </Form.Group>

              <div className="mt-2 d-flex gap-2">
                <Button variant="danger" onClick={handleReject}>
                  Confirm Reject
                </Button>

                <Button
                  variant="secondary"
                  onClick={() => setShowRejectBox(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* CHILD MODALS */}
      <AssignAppointmentModal
        show={assignShow}
        onHide={() => setAssignShow(false)}
        appointment={localAppointment}
        onAssign={handleAssign}
      />

      <PaymentModal
        show={paymentShow}
        onHide={() => setPaymentShow(false)}
        appointment={localAppointment}
        onSave={handleSavePayment}
      />
    </>
  );
};

export default ViewAppointmentModal;
